<template>
  <div class="feedback-display">
    <h4 class="black--text mb-2">{{ t("feedback") }}</h4>

    <!-- Existing feedback -->
    <div v-if="feedback">
      <div class="d-flex align-center">
        <v-avatar :color="feedback.agree ? 'green' : 'red'" size="44" class="mr-3">
          <v-icon dark>{{ feedback.agree ? "mdi-thumb-up" : "mdi-thumb-down" }}</v-icon>
        </v-avatar>
        <span
          class="font-weight-medium"
          :class="feedback.agree ? 'green--text' : 'red--text'"
        >
          {{ feedback.agree ? t("agreedWithPrediction") : t("disagreedWithPrediction") }}
        </span>
      </div>

      <p v-if="feedback.message" class="mt-2 grey--text text--darken-2">{{ feedback.message }}</p>

      <div v-if="feedback.attachments && feedback.attachments.length" class="mt-4">
        <div class="black--text font-weight-medium mb-2">{{ t("attachments") }}</div>
        <div class="fb-att-grid">
          <div v-for="att in feedback.attachments" :key="att.id" class="fb-att">
            <a
              class="fb-att-card"
              :href="att.url"
              :download="att.fileName || true"
              target="_blank"
              rel="noopener"
              :title="t('download')"
            >
              <v-icon class="fb-att-download" color="green darken-2" small>mdi-download</v-icon>
              <span class="fb-att-type">{{ fileLabel(att) }}</span>
            </a>
            <div class="fb-att-name green--text text--darken-2">{{ displayName(att) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- No feedback yet: agree/disagree prompt. Emits `add` with the chosen
         value; the parent opens the feedback dialog preset to it. -->
    <div v-else>
      <div class="black--text font-weight-bold text-subtitle-1 mb-3">
        {{ t("doYouAgree") }}
      </div>
      <div class="d-flex">
        <div class="mr-6 text-center">
          <v-btn
            fab
            depressed
            outlined
            color="grey"
            class="fb-prompt-choice"
            @click="$emit('add', true)"
          >
            <v-icon color="grey darken-1">mdi-thumb-up</v-icon>
          </v-btn>
          <div class="mt-1 grey--text text--darken-2 text-body-2">{{ t("agree") }}</div>
        </div>
        <div class="text-center">
          <v-btn
            fab
            depressed
            outlined
            color="grey"
            class="fb-prompt-choice"
            @click="$emit('add', false)"
          >
            <v-icon color="grey darken-1">mdi-thumb-down</v-icon>
          </v-btn>
          <div class="mt-1 grey--text text--darken-2 text-body-2">{{ t("disagree") }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Reusable read-only AI-report feedback summary. Shows the submitted feedback
 * (agree/disagree + message + downloadable attachment cards), or — when none yet
 * — an agree/disagree prompt that emits `add(agree)` with the chosen value so the
 * parent can open the feedback dialog preset to it. Localized via the
 * `i18nPrefix` prop so it is shared across report modules.
 */
export default {
  name: "AiReportFeedbackDisplay",
  props: {
    // report.feedback (null when none submitted)
    feedback: { type: Object, default: null },
    i18nPrefix: { type: String, required: true },
  },
  methods: {
    t(key) {
      return this.$t(`${this.i18nPrefix}.${key}`);
    },
    // File-type badge label, e.g. "PNG" / "MP4".
    fileLabel(att) {
      const name = att && att.fileName ? String(att.fileName) : "";
      const dot = name.lastIndexOf(".");
      if (dot > -1 && dot < name.length - 1) {
        return name.slice(dot + 1).toUpperCase();
      }
      if (att && att.mimeType && att.mimeType.includes("/")) {
        return att.mimeType.split("/")[1].toUpperCase();
      }
      return (att && att.fileType ? att.fileType : "file").toUpperCase();
    },
    // File name without its extension (matches the design caption).
    displayName(att) {
      const name = att && att.fileName ? String(att.fileName) : "";
      if (name) {
        const dot = name.lastIndexOf(".");
        return dot > 0 ? name.slice(0, dot) : name;
      }
      return att && att.fileType ? att.fileType : "attachment";
    },
  },
};
</script>

<style scoped>
.fb-prompt-choice {
  width: 52px !important;
  height: 52px !important;
}
.fb-att-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.fb-att {
  width: 110px;
}
.fb-att-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 110px;
  height: 90px;
  background: #eefaf2;
  border: 1px solid #d6efe0;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.15s ease;
}
.fb-att-card:hover {
  background: #e2f5ea;
}
.fb-att-download {
  position: absolute;
  top: 8px;
  right: 8px;
}
.fb-att-type {
  font-weight: bold;
  font-size: 20px;
  color: #1b5e20;
  letter-spacing: 0.5px;
}
.fb-att-name {
  margin-top: 6px;
  font-size: 13px;
  word-break: break-all;
}
</style>
